import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { LabelFieldComponent, LabelTextareaComponent } from "../../../components/fields";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import Swal from 'sweetalert2';
import "./ProductEditPage.css"; // Optional: for toggle styling
import { useNavigate } from "react-router-dom";

export default function ProductEditPage() {
    const { t } = useContext(TranslatorContext);
    const { slug } = useParams();

    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState(null);
    const [categories, setCategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vendorSearch, setVendorSearch] = useState("");
    const [showVendorDropdown, setShowVendorDropdown] = useState(false);
    const vendorDropdownRef = useRef(null);
    const [uploadFileCard, setUploadFileCard] = useState("Select Card Image");
    const [uploadFileDetail, setUploadFileDetail] = useState("Select Detail Image");

    const [formData, setFormData] = useState({
        name: "",
        cat_id: "",
        vendor_id: "",
        details: "",
        is_online: false,
        card_img: null,
        detail_img: null,
    });

    useEffect(() => {
        const token = localStorage.getItem("authToken");
        const headers = {
            Authorization: `Bearer ${token}`
        };

        const productRequest = axios.get(`${process.env.REACT_APP_API_URL}/products-edit/${slug}`, { headers });
        const vendorsRequest = axios.get(`${process.env.REACT_APP_API_URL}/products-create`, { headers });

        Promise.all([productRequest, vendorsRequest])
            .then(([productRes, createRes]) => {
                const data = productRes.data.product;
                const vendorsData = createRes.data.vendors || [];
                const categoriesFromProduct = productRes.data.categories || [];
                const categoriesFromCreate = createRes.data.categories || [];
                const categoriesData = categoriesFromProduct.length ? categoriesFromProduct : categoriesFromCreate;

                setProduct(data);
                setCategories(categoriesData);
                setVendors(vendorsData);
                setFormData({
                    name: data.name,
                    cat_id: data.cat_id || "",
                    vendor_id: data.vendor_id || "",
                    details: data.details || "",
                    is_online: data.is_online ? true : false,
                    card_img: null,
                    detail_img: null,
                });

                const selectedVendor = vendorsData.find(vendor => vendor.id === data.vendor_id);
                setVendorSearch(selectedVendor ? (selectedVendor.company_name ? `${selectedVendor.company_name} (${selectedVendor.name})` : selectedVendor.name) : "");
            })
            .catch((err) => {
                console.error(err);
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to load product data'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            })
            .finally(() => setLoading(false));
    }, [slug, t]);

    const handleChange = (e) => {
        const { name, type, value, checked, files } = e.target;
        if (type === "checkbox") {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else if (type === "file") {
            const file = files[0];
            setFormData((prev) => ({ ...prev, [name]: file }));
            if (name === "card_img") setUploadFileCard(file?.name || "Select Card Image");
            if (name === "detail_img") setUploadFileDetail(file?.name || "Select Detail Image");
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleVendorSearch = (e) => {
        setVendorSearch(e.target.value);
        setShowVendorDropdown(true);
    };

    const selectVendor = (vendor) => {
        setFormData((prev) => ({ ...prev, vendor_id: vendor.id }));
        setVendorSearch(vendor.company_name ? `${vendor.company_name} (${vendor.name})` : vendor.name);
        setShowVendorDropdown(false);
    };

    const clearVendorSelection = () => {
        setFormData((prev) => ({ ...prev, vendor_id: "" }));
        setVendorSearch("");
        setShowVendorDropdown(false);
    };

    const filteredVendors = (vendors || []).filter((vendor) => {
        const searchTerm = vendorSearch.toLowerCase();
        const vendorName = vendor.name.toLowerCase();
        const companyName = vendor.company_name ? vendor.company_name.toLowerCase() : "";
        return vendorName.includes(searchTerm) || companyName.includes(searchTerm);
    });

    const navigate = useNavigate();

    const handleSubmit = async () => {
        // Validation
        if (!formData.name) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('Please enter product title'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
            return;
        }

        if (!formData.cat_id) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('Please select a category'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
            return;
        }

        if (!formData.vendor_id) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('Please select a vendor'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
            return;
        }

        const payload = new FormData();
        Object.keys(formData).forEach((key) => {
            let value = formData[key];
            if (key === "is_online") value = value ? 1 : 0;
            if (value !== null && value !== undefined) payload.append(key, value);
        });

        // Add _method for PUT request if your backend expects it
        payload.append('_method', 'PUT');

        // Show loading toast
        Swal.fire({
            title: t('Updating product...'),
            text: t('Please wait'),
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/products-update/${slug}`,
                payload,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "Authorization": `Bearer ${localStorage.getItem("authToken")}`
                    },
                }
            );

            // Close loading alert
            Swal.close();

            // Success toast
            Swal.fire({
                icon: 'success',
                title: t('success'),
                text: t('Product updated successfully'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });

            // Navigate after toast
            setTimeout(() => {
                navigate("/products-list");
            }, 1500);

        } catch (error) {
            // Close loading alert
            Swal.close();

            console.error('Error updating product:', error);

            if (error.response?.status === 422) {
                const errors = error.response.data.errors;
                let errorMessage = '';

                if (errors.name) errorMessage += errors.name[0] + '\n';
                if (errors.cat_id) errorMessage += errors.cat_id[0] + '\n';
                if (errors.details) errorMessage += errors.details[0] + '\n';
                if (errors.card_img) errorMessage += errors.card_img[0] + '\n';
                if (errors.detail_img) errorMessage += errors.detail_img[0] + '\n';

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: errorMessage || t('Please check the form for errors'),
                    confirmButtonText: t('ok')
                });
            } else if (error.response?.data?.message) {
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.message,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to update product. Please try again.'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        }
    };

    if (loading) return <PageLayout><p>{t("loading")}...</p></PageLayout>;
    if (!product) return <PageLayout><p>{t("product_not_found")}</p></PageLayout>;

    return (
        <PageLayout>
            <Row>
                {/* Left Form */}
                <Col xl={7}>
                    <div className="mc-card mb-4 p-4">
                        <h4 className="mb-4">{t("Edit Product")} - {formData.name}</h4>

                        <LabelFieldComponent
                            type="text"
                            label={t("title")}
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            fieldSize="mb-4 w-100 h-md"
                        />

                        <LabelTextareaComponent
                            label={t("description")}
                            name="details"
                            value={formData.details}
                            onChange={handleChange}
                            fieldSize="mb-4 w-100 h-text-md"
                        />

                        <select
                            name="cat_id"
                            value={formData.cat_id}
                            onChange={handleChange}
                            className="mb-4 w-100 h-md form-select"
                        >
                            <option value="">{t("Select Category")}</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>

                        <div className="mb-4">
                            <label className="form-label fw-bold">{t("Vendor")}</label>
                            <div className="position-relative" ref={vendorDropdownRef}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t("Search and select vendor")}
                                    value={vendorSearch}
                                    onChange={handleVendorSearch}
                                    onFocus={() => setShowVendorDropdown(true)}
                                    autoComplete="off"
                                />
                                {formData.vendor_id && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary position-absolute"
                                        style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                                        onClick={clearVendorSelection}
                                        title="Clear selection"
                                    >
                                        ×
                                    </button>
                                )}
                                {showVendorDropdown && (
                                    <div className="position-absolute w-100 bg-white border rounded shadow-sm" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                                        {filteredVendors.length > 0 ? (
                                            filteredVendors.map(vendor => (
                                                <div
                                                    key={vendor.id}
                                                    className="p-2"
                                                    onClick={() => selectVendor(vendor)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="fw-bold">{vendor.name}</div>
                                                    {vendor.company_name && (
                                                        <small className="text-muted">{vendor.company_name}</small>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-2 text-muted">{t("No vendors found")}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Beautiful Toggle */}
                        <div className="toggle-wrapper mb-4">
                            <label className="toggle-label">{t("Is Online")}</label>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    name="is_online"
                                    checked={formData.is_online}
                                    onChange={handleChange}
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>
                    </div>
                </Col>

                {/* Right Media */}
                <Col xl={5}>
                    <div className="mc-card mb-4 p-4">
                        <h4 className="mb-4">{t("Product Gallery")}</h4>

                        {/* Existing images */}
                        {product.card_img && (
                            <div className="mb-3">
                                <label className="form-label fw-bold">{t("Current Card Image")}</label>
                                <div>
                                    <img
                                        src={`${process.env.REACT_APP_IMG_URL}${product.card_img}`}
                                        alt="card"
                                        className="mb-2 rounded"
                                        style={{
                                            width: '200px',
                                            height: '150px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {product.detail_img && (
                            <div className="mb-3">
                                <label className="form-label fw-bold">{t("Current Detail Image")}</label>
                                <div>
                                    <img
                                        src={`${process.env.REACT_APP_IMG_URL}${product.detail_img}`}
                                        alt="detail"
                                        className="mb-2 rounded"
                                        style={{
                                            width: '200px',
                                            height: '150px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Upload new images */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">{t("Change Card Image")}</label>
                            <input
                                type="file"
                                id="card_img"
                                name="card_img"
                                onChange={handleChange}
                                className="form-control"
                                accept="image/*"
                            />
                            <small className="text-muted">{t("Leave empty to keep current image")}</small>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-bold">{t("Change Detail Image")}</label>
                            <input
                                type="file"
                                id="detail_img"
                                name="detail_img"
                                onChange={handleChange}
                                className="form-control"
                                accept="image/*"
                            />
                            <small className="text-muted">{t("Leave empty to keep current image")}</small>
                        </div>

                        <ButtonComponent
                            className="mc-btn primary mt-3 w-100"
                            onClick={handleSubmit}
                            text={t("update_product")}
                        />
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}